import {
  IBackendCartEntries,
  ICartEntry,
  ICombo,
  IComboSlotOption,
  IItem,
  IItemOption,
  IItemOptionModifier,
  IModifierSelection,
  IPrices,
  ISanityItemOption
} from '@rbi-ctg/menu';

import {
  IWithVendorConfig,
  PluTypes,
  PosVendors,
  getVendorConfig,
  reduceMultiConstantPluQuantity
} from '../vendor-config';

import { isDefaultOrLess } from './modifiers';
import { computePluForItemOptionModifier } from './plu';
import brandedPriceForCartEntry from './price-for-cart-entry';

import { assignInclusionComboSlotSelections, computeSelectedOption } from '.';

import { compose, formatCurrency } from '..';

export type Selections = Array<{
  notIncluded?: number;
  option: IComboSlotOption;
  quantity: number;
}>;

interface IComboSlotSelections {
  selections: Selections;
}

type PricingFunction = (
  item: IWithVendorConfig,
  quantity?: number,
  modifierSelections?: IModifierSelection[]
) => number;

interface IComputePriceOptions {
  comboSlotSelections: {
    [key: string]: IComboSlotSelections;
  };
  item: IWithVendorConfig;
  modifierSelections: IModifierSelection[];
  pickerSelections: {
    [identifier: string]: string;
  };
  posVendor: null | PosVendors;
  pricingFunction: PricingFunction;
  quantity: number;
}

type Totals = [number, number];

function computeItemPrice({
  item,
  modifierSelections,
  posVendor,
  pricingFunction,
  quantity,
  quantityExtra
}: {
  item: IWithVendorConfig;
  modifierSelections: IModifierSelection[];
  posVendor: PosVendors | null;
  pricingFunction: PricingFunction;
  quantity: number;
  quantityExtra: number;
}): Totals {
  if (!item.vendorConfigs || !posVendor) {
    return [0, 0];
  }

  const { [posVendor]: vendorConfig } = item.vendorConfigs;
  if (vendorConfig && vendorConfig.pluType === PluTypes.QUANTITY) {
    const realTotal = pricingFunction(item, quantity);
    const quantityBaseTotal = Math.floor(realTotal / quantity);
    const quantityExtraTotal = realTotal - quantityBaseTotal;

    return [quantityBaseTotal, quantityExtraTotal];
  }

  const baseTotal = pricingFunction(item, 1, modifierSelections);
  const extraTotal = quantityExtra ? pricingFunction(item, quantityExtra, modifierSelections) : 0;
  return [baseTotal, extraTotal];
}

const QUANTITY_ONE = 1;

function computeComboPrice({
  combo,
  comboSlotSelections = {},
  modifierSelections,
  pricingFunction,
  quantity,
  quantityExtra
}: {
  combo: ICombo;
  comboSlotSelections: {
    [key: string]: IComboSlotSelections;
  };
  modifierSelections: IModifierSelection[];
  pricingFunction: PricingFunction;
  quantity: number;
  quantityExtra: number;
}): Totals {
  // price main item and main item modifiers
  const baseTotal = pricingFunction(
    combo,
    quantity,
    (modifierSelections || []).filter(({ comboSlotId }) => !comboSlotId)
  );
  // price upsell combo slot items and combo slot modifiers
  const upsellSlotsTotal = combo.options.reduce((acc, option) => {
    const { selections } = comboSlotSelections[option._id] || {};
    if (selections) {
      const assignedSelections: Selections = assignInclusionComboSlotSelections(
        selections,
        option.maxAmount
      );

      const comboTotals = assignedSelections.reduce((total, sel) => {
        // the price for the combo slot selection
        const baseItemPrice = pricingFunction(sel.option.option, QUANTITY_ONE);
        // the price for all selections not included in the combo
        const upsellItemPrice = sel.notIncluded
          ? pricingFunction(sel.option.option, sel.notIncluded)
          : 0;
        // the price for the selected modifiers less the price
        // of the selection itself
        const modifiersPrice =
          pricingFunction(
            sel.option.option,
            QUANTITY_ONE,
            (modifierSelections || []).filter(({ comboSlotId }) => comboSlotId === option._id)
          ) - baseItemPrice;

        return total + upsellItemPrice + modifiersPrice * sel.quantity;
      }, 0);

      return acc + comboTotals;
    }

    return acc;
  }, 0);

  const extraTotal = baseTotal * quantityExtra + upsellSlotsTotal * quantity;

  return [baseTotal, extraTotal];
}

export function computeCurrentPrice({
  comboSlotSelections = {},
  item,
  modifierSelections = [],
  pickerSelections,
  posVendor,
  pricingFunction,
  quantity = 1
}: IComputePriceOptions): Totals {
  const quantityExtra = Math.max(0, quantity - 1);

  if (item._type === 'item') {
    return computeItemPrice({
      item,
      modifierSelections,
      posVendor,
      pricingFunction,
      quantity,
      quantityExtra
    });
  }

  const selection: ICombo | IItem = computeSelectedOption(pickerSelections, item);

  if (selection) {
    if (selection._type === 'item') {
      return computeItemPrice({
        item: selection,
        modifierSelections,
        posVendor,
        pricingFunction,
        quantity,
        quantityExtra
      });
    }

    if (selection._type === 'combo') {
      return computeComboPrice({
        combo: selection,
        comboSlotSelections,
        modifierSelections,
        pricingFunction,
        quantity,
        quantityExtra
      });
    }
  }

  return [0, 0];
}

interface IPriceItemOptionModifierPlu {
  item: IBackendCartEntries | ICartEntry | IItem;
  modifier: IBackendCartEntries | ICartEntry | (IItemOptionModifier & { quantity?: number });
  prices: IPrices;
  vendor: PosVendors | null;
}

interface IPriceItemOptionModifier {
  item: IItem | ICartEntry;
  itemOption: ISanityItemOption | IItemOption;
  modifier: IItemOptionModifier & { quantity?: number };
  prices: IPrices;
  vendor: PosVendors;
}

export function priceForItemOptionModifierPlu({
  item,
  modifier,
  prices,
  vendor
}: IPriceItemOptionModifierPlu): number {
  const plu = computePluForItemOptionModifier({
    item,
    modifier,
    vendor,
    prices
  });

  // if plu could not be computed, we cannot get
  //  a price for this modifier
  if (!plu) {
    return 0;
  }

  const price = plu in prices ? prices[plu] : 0;

  const modifierVendorConfig = getVendorConfig(modifier, vendor);

  if (!modifierVendorConfig) {
    return 0;
  }

  // if modifier is a multi-constant plu, we need to use
  // the quantity indicated by the plu to determine price
  if (modifierVendorConfig.pluType === PluTypes.MULTI_CONSTANT) {
    const quantity = reduceMultiConstantPluQuantity(modifierVendorConfig.multiConstantPlus || []);

    return price * quantity;
  }

  return price * (modifier.quantity || 1);
}

export function priceForItemOptionModifierBooleanOrStepper({
  item,
  itemOption,
  modifier,
  prices,
  vendor
}: IPriceItemOptionModifier): number {
  // we do not charge the user for modifiers that are
  // default or any modifier with a smaller or equal
  // multiplier to the default
  const defaultOrLess = isDefaultOrLess(itemOption, modifier);

  if (defaultOrLess && !itemOption.upsellModifier) {
    return 0;
  }

  return priceForItemOptionModifierPlu({
    item,
    modifier,
    prices,
    vendor
  });
}

export function priceForItemOptionModifierSelection({
  item,
  itemOption,
  modifier,
  prices,
  vendor
}: IPriceItemOptionModifier): number {
  if (itemOption.allowMultipleSelections) {
    // @todo implement pricing for allowMultipleSelections
    return 0;
  }

  if (modifier.default && !itemOption.upsellModifier) {
    return 0;
  }

  return priceForItemOptionModifierPlu({
    item,
    modifier,
    prices,
    vendor
  });
}

export const priceForCartEntry = brandedPriceForCartEntry;

export const displayCartEntryPrice = compose(formatCurrency, priceForCartEntry);
